import type { ApiResult } from '../types';
import type { ExtraditionMlaWizardDraft } from '../types/extraditionMlaCaseRequest';
import type { ExtraditionMlaStepsResponse } from '../types/extraditionMlaApi';
import type { DocumentChecklistItem, LookupItem } from '../mocks/criminalMasterData';
import {
  type ExtraditionMlaWizardStep,
  setExtraditionMlaCategoryId,
  isExtraditionMlaCategory,
} from '../pages/wizard/extraditionMla/extraditionMlaConstants';
import { DEFAULT_EXTRADITION_MLA_DRAFT } from '../layouts/ExtraditionMlaRequestLayout';
import { caseApiRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import * as criminalService from './criminalCaseRequests';
import { cachedCriminalFetch } from './criminalLookupCache';
import {
  buildSaveExtraditionMlaPayload,
  caseRequestIdFromResponse,
  mapExtraditionMlaStepsToDraft,
  referenceNoFromResponse,
  resolveExtraditionMlaDraftResumeStep,
} from './extraditionMlaCaseMapper';

export const getFileCategories = criminalService.getFileCategories;
export const getSubFileCategories = criminalService.getSubFileCategories;
export const getSubCategoryApplicableAreas = criminalService.getSubCategoryApplicableAreas;
export const getProvinces = criminalService.getProvinces;
export const getDistricts = criminalService.getDistricts;
export const getUrgencyLevels = criminalService.getUrgencyLevels;
export const getDeliveryMethods = criminalService.getDeliveryMethods;
export const getGenders = criminalService.getGenders;
export const getDocumentChecklist = criminalService.getDocumentChecklist;
export const uploadCaseFile = criminalService.uploadCaseFile;
export const downloadCaseFile = criminalService.downloadCaseFile;
export {
  getCommonCoverNote as getExtraditionMlaCoverNote,
  downloadCommonCoverNotePdf as downloadExtraditionMlaCoverNotePdf,
} from './coverNoteRequests';

/** Resolve Extradition & MLA category id from master data when available. */
export async function resolveExtraditionMlaCategoryId(): Promise<string> {
  const res = await getFileCategories();
  if (!res.success) return '';
  const match = res.data.find((c) => isExtraditionMlaCategory(c));
  if (match?.id) setExtraditionMlaCategoryId(match.id);
  return match?.id ?? '';
}

function parseLookupRows(data: unknown): LookupItem[] {
  if (!Array.isArray(data)) return [];
  const rows: LookupItem[] = [];
  for (const entry of data) {
    if (!entry || typeof entry !== 'object') continue;
    const r = entry as Record<string, unknown>;
    if (r.active === false) continue;
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    const id =
      typeof r.id === 'string'
        ? r.id.trim()
        : typeof r.code === 'string'
          ? r.code.trim()
          : '';
    if (!name || !id) continue;
    rows.push({
      id,
      name,
      code: typeof r.code === 'string' ? r.code : undefined,
    });
  }
  return rows;
}

/** CMS — countries for extradition requests (GET /v1/case-requests/countries) */
export async function getCountries(): Promise<ApiResult<LookupItem[]>> {
  return cachedCriminalFetch('countries', async () => {
    const res = await caseApiRequest<unknown>('/countries');
    if (!res.ok) return { success: false, message: res.message };
    return { success: true, data: parseLookupRows(res.data) };
  });
}

/** CMS-MNA-1 — GET /v1/case-requests/nationalities */
export const getNationalities = criminalService.getNationalities;

/** CMS-MCC-1 — current custody status */
export const getCustodyStatuses = criminalService.getCurrentCustodyStatuses;

export type SaveExtraditionMlaResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

/** CMS — save/update extradition & MLA steps 1–6 */
export async function saveExtraditionMlaRequest(
  draft: ExtraditionMlaWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: ExtraditionMlaWizardStep;
    allSteps?: boolean;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SaveExtraditionMlaResult>> {
  const body = buildSaveExtraditionMlaPayload(draft, {
    isDraft: options.isDraft,
    currentStep: options.currentStep,
    allSteps: options.allSteps,
    confirmedCorrect: options.confirmedCorrect,
    isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
  });

  const res = await caseApiRequest<ExtraditionMlaStepsResponse>('/extradition-mla/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || draft.draftRequestId;

  return {
    success: true,
    data: {
      requestId,
      fileReferenceNumber: referenceNoFromResponse(res.data),
    },
  };
}

export type ExtraditionMlaRequestViewData = {
  draft: ExtraditionMlaWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step1?: ExtraditionMlaStepsResponse['step_1'];
  step2?: ExtraditionMlaStepsResponse['step_2'];
  step3?: ExtraditionMlaStepsResponse['step_3'];
  step4?: ExtraditionMlaStepsResponse['step_4'];
  step5?: ExtraditionMlaStepsResponse['step_5'];
  step6?: ExtraditionMlaStepsResponse['step_6'];
};

export async function getExtraditionMlaRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<ExtraditionMlaRequestViewData | null>> {
  if (!caseRequestId.trim()) return { success: true, data: null };

  const res = await caseApiRequest<ExtraditionMlaStepsResponse>(
    caseRequestStepsPath(`/extradition-mla/steps/${encodeURIComponent(caseRequestId)}`, options),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const draft = mapExtraditionMlaStepsToDraft(res.data, {
    ...DEFAULT_EXTRADITION_MLA_DRAFT,
    draftRequestId: caseRequestIdFromResponse(res.data) || caseRequestId,
  });

  const statusName = (res.data.status_name ?? res.data.status_code ?? '').trim();
  const submittedAt = (res.data.created_at ?? res.data.updated_at ?? res.data.updatedAt ?? '').trim();
  const raw = res.data as { completion_remarks?: string };

  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step1: res.data.step_1,
      step2: res.data.step_2,
      step3: res.data.step_3,
      step4: res.data.step_4,
      step5: res.data.step_5,
      step6: res.data.step_6,
    },
  };
}

export async function getExtraditionMlaRequestDetails(
  caseRequestId: string,
): Promise<ApiResult<ExtraditionMlaWizardDraft | null>> {
  const view = await getExtraditionMlaRequestViewData(caseRequestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

export async function getExtraditionMlaDraftResumeStep(
  caseRequestId: string,
): Promise<ApiResult<{ step: ExtraditionMlaWizardStep; requestId: string }>> {
  if (!caseRequestId.trim()) return { success: false, message: 'Case request id is missing.' };

  const res = await caseApiRequest<ExtraditionMlaStepsResponse>(
    `/extradition-mla/steps/${encodeURIComponent(caseRequestId)}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || caseRequestId;
  return {
    success: true,
    data: { step: resolveExtraditionMlaDraftResumeStep(res.data), requestId },
  };
}

export async function submitExtraditionMlaRequest(
  draft: ExtraditionMlaWizardDraft,
): Promise<ApiResult<SaveExtraditionMlaResult>> {
  return saveExtraditionMlaRequest(draft, {
    isDraft: false,
    allSteps: true,
    confirmedCorrect: draft.confirmCorrect,
  });
}
